import _ from 'lodash';
import { snakeCase, snakeCasedOrgPartHelper } from 'utils/functions';
import {
  QueryParams,
  OperatorMap,
  NullOperatorValues,
  OrderDirectionValues,
  FilterLinkOperatorsValues,
  FieldFilter,
  ALL,
  CORE,
  ListResponse,
  GenericResource,
} from 'types/api';
import { GenericObject } from 'types';

export function isNullOperator(operator: string) {
  return Object.keys(NullOperatorValues).includes(operator);
}

export function isFilterLinkOperator<T extends GenericResource>(
  filter: FieldFilter<T> | keyof typeof FilterLinkOperatorsValues
) {
  return Object.keys(FilterLinkOperatorsValues).includes(filter as string);
}

export function isFieldFilter<T extends GenericResource>(
  filter: FieldFilter<T> | keyof typeof FilterLinkOperatorsValues
): filter is FieldFilter<T> {
  return (filter as FieldFilter<T>).field !== undefined;
}

export function getNullOperatorValue(operator: string) {
  return NullOperatorValues[operator as keyof typeof NullOperatorValues];
}

export function getOperator(operator: string) {
  return OperatorMap[operator as keyof typeof OperatorMap];
}

export function parseNestedField(field: string | symbol | number) {
  return field
    .toString()
    .split('.')
    .reduce((acc, fieldPart, index) => {
      const snakeCasedFieldPart = snakeCasedOrgPartHelper(fieldPart);
      return `${acc}${index !== 0 ? '__' : ''}${snakeCasedFieldPart}`;
    }, '');
}

export function getFilterExpression<T extends GenericResource>(
  filterItem: FieldFilter<T>
) {
  const { field, operator, value } = filterItem;
  return `${parseNestedField(field)}${getOperator(operator)}:${
    isNullOperator(operator) ? getNullOperatorValue(operator) : getValue(value)
  }`;
}

export function getFilterLinkOperator(
  operator: keyof typeof FilterLinkOperatorsValues
) {
  return FilterLinkOperatorsValues[operator];
}

export function getValue(value: any) {
  if (Array.isArray(value)) {
    return value.map((item) => encodeURIComponent(item)).join(';');
  }
  return encodeURIComponent(value);
}

export function getFilterParam<T extends GenericResource>(
  filters: QueryParams<T>['filters'] | QueryParams<T>['excludes']
) {
  return (
    filters
      // item is either a Filter or a Link operator, or undefined and not a null operator
      ?.filter(
        (item) =>
          isFilterLinkOperator(item) ||
          (isFieldFilter(item) &&
            (!_.isNull(item.value) || isNullOperator(item.operator)) &&
            (isNullOperator(item.operator) || !_.isUndefined(item.value)) &&
            (_.isArray(item.value) ? item.value.length > 0 : true))
      )
      ?.reduce((acc: any, item: any, index: number, originalArray) => {
        if (isFilterLinkOperator(item)) {
          // if the item is a link operator, update the previous element in the array with its character
          acc[acc.length - 1] = `${acc[acc.length - 1]}${getFilterLinkOperator(
            item
          )}`;
          return acc;
        }

        if (acc.length === 0) {
          acc.push(getFilterExpression<T>(item));
          return acc;
        }
        // if the item is a filter, and the previous item is a link operator, update the previous element in the array with the filter expression
        if (
          originalArray[index - 1] &&
          isFilterLinkOperator(originalArray[index - 1])
        ) {
          acc[acc.length - 1] = `${acc[acc.length - 1]}${getFilterExpression<T>(
            item
          )}`;
          return acc;
        }

        // if the item is a filter, and the previous item is not a link operator, add the filter expression to the array
        acc.push(getFilterExpression<T>(item));
        return acc;
      }, [])
      ?.join(',')
  );
}

// TODO: properly type this
export function buildSchemaString(subSchema: any) {
  return subSchema
    .map((item: any) => {
      if (typeof item === 'string' && _.includes(item, 'custom_properties')) {
        return `custom_properties__${_.chain(item)
          .replace('custom_properties__', '')
          .snakeCase()
          .value()}`;
      }
      if (typeof item === 'string') {
        const snakeCasedItem = snakeCasedOrgPartHelper(item);
        return item === ALL || item === CORE ? item : snakeCasedItem;
      }
      return Object.keys(item)
        .map((key) => {
          const snakeCasedKey = snakeCasedOrgPartHelper(key);
          return `${snakeCasedKey}{${buildSchemaString(item[key])}}`;
        })
        .join(',');
    })
    .join(',');
}

export function getSchemaParam<T extends GenericResource>(
  schema: QueryParams<T>['schema']
) {
  if (!schema) {
    return 'schema=__all__';
  }

  if (typeof schema === 'string') {
    return `schema=${schema}`;
  }

  return `schema=${buildSchemaString(schema)}`;
}

export function getSearchSchemaParam<T extends GenericResource>(
  schema: QueryParams<T>['searchSchema']
) {
  if (!schema) {
    return 'search_schema=__all__';
  }

  if (typeof schema === 'string') {
    return `search_schema=${schema}`;
  }

  return `search_schema=${buildSchemaString(schema)}`;
}

// TODO: probably handle negative page number
export function getPageNumberParam<T extends GenericResource>(
  pageNumber: QueryParams<T>['pageNumber']
) {
  return `page_number=${pageNumber}`;
}

// TODO: probably handle negative page size
export function getPageSizeParam<T extends GenericResource>(
  pageSize: QueryParams<T>['pageSize']
) {
  return `page_size=${pageSize}`;
}

export function getSortParam<T extends GenericResource>(
  sort: QueryParams<T>['sort']
) {
  return `order_by=${sort?.map(
    (item) =>
      `${
        OrderDirectionValues[
          item.orderDirection as keyof typeof OrderDirectionValues
        ]
      }${parseNestedField(item.orderBy)}`
  )}`;
}

export function getNoCacheParam<T extends GenericResource>(
  noCache: QueryParams<T>['noCache']
) {
  return noCache ? 'no_cache' : '';
}

export function getNewVersionParam<T extends GenericResource>(
  newVersion: QueryParams<T>['newVersion']
) {
  return newVersion ? 'new' : '';
}

export function getExportParam<T extends GenericResource>(
  exportBlob: QueryParams<T>['exportBlob']
) {
  return exportBlob ? 'export' : '';
}

export function getDistinctParam<T extends GenericResource>(
  distinct: QueryParams<T>['distinct']
) {
  return distinct ? 'distinct' : '';
}

export function parseQueryParams<T extends GenericResource>({
  pageSize,
  pageNumber = 1,
  sort,
  filters,
  excludes,
  schema,
  searchSchema,
  search,
  noCache = false,
  newVersion = false,
  exportBlob = false,
  distinct = false,
}: QueryParams<T>) {
  const queryParams = [];
  if (pageSize !== undefined) {
    queryParams.push(getPageSizeParam<T>(pageSize));
  }
  if (pageNumber) {
    queryParams.push(getPageNumberParam<T>(pageNumber));
  }
  if (sort && sort.length > 0) {
    queryParams.push(getSortParam<T>(sort));
  }
  if (excludes && excludes?.length > 0) {
    queryParams.push(
      `excludes=${getFilterParam<T>(
        snakeCase(excludes) as QueryParams<T>['excludes']
      )}`
    );
  }
  if (filters && filters?.length > 0) {
    queryParams.push(
      `filters=${getFilterParam<T>(
        snakeCase(filters) as QueryParams<T>['filters']
      )}`
    );
  }
  if (searchSchema) {
    queryParams.push(getSearchSchemaParam<T>(searchSchema));
  }
  if (search) {
    queryParams.push(`search=${encodeURIComponent(search)}`);
  }
  if (noCache) {
    queryParams.push(getNoCacheParam<T>(noCache));
  }
  if (newVersion) {
    queryParams.push(getNewVersionParam<T>(newVersion));
  }
  if (exportBlob) {
    queryParams.push(getExportParam<T>(exportBlob));
  }
  if (distinct) {
    queryParams.push(getDistinctParam<T>(distinct));
  }
  queryParams.push(getSchemaParam<T>(schema));

  return `?${queryParams.join('&')}`;
}

export const transformResponseToFallbackNullOrgPartToPart = <
  T extends GenericObject
>(
  response: ListResponse<T>
) =>
  ({
    ...response,
    data: _.map(response.data, (item) => {
      if (!item.orgPart && item.part) {
        return { ...item, orgPart: item.part };
      }
      return item;
    }),
  } as ListResponse<T>);

/**
 * Extracts overrideOrgId from params and returns the cleaned parameters
 * Handles both object and array parameters safely
 */
export function processOverrideOrgIdParams<T extends object>(
  params: T,
  activeOrgId: string | null
): {
  orgId: string | null;
  paramsWithoutOverride: Omit<T, 'overrideOrgId'>;
} {
  // If params is an array, return as-is since arrays don't have overrideOrgId
  if (Array.isArray(params)) {
    return {
      orgId: activeOrgId,
      paramsWithoutOverride: params as Omit<T, 'overrideOrgId'>,
    };
  }

  // Handle object parameters
  if ('overrideOrgId' in params) {
    const { overrideOrgId, ...rest } = params as T & {
      overrideOrgId?: string;
    };
    return {
      orgId: overrideOrgId || activeOrgId,
      paramsWithoutOverride: rest as Omit<T, 'overrideOrgId'>,
    };
  }

  return {
    orgId: activeOrgId,
    paramsWithoutOverride: params as Omit<T, 'overrideOrgId'>,
  };
}
